/**
 * Web PlatformIO implementation.
 *
 * - pickFiles: <input type=file> (File System Access API used when available).
 * - exportBundle: builds a zip in-memory and triggers a download.
 * - download: anchor download.
 * - localStore: IndexedDB-backed (falls back to memory when unavailable).
 * - deviceFingerprint: a stable browser fingerprint persisted in localStore.
 */
import { isAllowedExternalUrl, refusedExternalUrl } from "./externalUrl.js";
import type {
  ConnectivityListener,
  ExportBundle,
  ExportBundleResult,
  LocalStore,
  PickFilesOptions,
  PickedFile,
  PlatformIO,
} from "./platform-io.js";
import { setCurrentPlatform } from "./platform-io.js";
import { createMemoryLocalStore } from "./stores/memory-store.js";
import { createZip } from "./zip.js";

const FINGERPRINT_KEY = "genz.device.fingerprint";

function validate(file: { size: number; type: string }, opts?: PickFilesOptions): void {
  if (opts?.maxBytes && file.size > opts.maxBytes) {
    throw new Error(`File exceeds max size ${opts.maxBytes} bytes`);
  }
  if (opts?.accept?.length && file.type && !opts.accept.includes(file.type)) {
    throw new Error(`File type ${file.type} not accepted`);
  }
}

export class WebPlatformIO implements PlatformIO {
  readonly kind = "web" as const;
  localStore: LocalStore;
  private listeners = new Set<ConnectivityListener>();

  constructor(localStore?: LocalStore) {
    // IndexedDB store would be injected here; memory store is the safe default
    // for SSR/tests. The web app wires the IndexedDB-backed store at startup.
    this.localStore = localStore ?? createMemoryLocalStore();
    if (typeof window !== "undefined") {
      window.addEventListener("online", () => this.emit(true));
      window.addEventListener("offline", () => this.emit(false));
    }
  }

  /** A new tab, with no `window.opener` and no referrer (see `PlatformIO.openExternal`). A
   *  popup blocker may still stop it; the caller keeps a link on screen for that case. */
  async openExternal(url: string): Promise<void> {
    if (!isAllowedExternalUrl(url)) throw refusedExternalUrl();
    if (typeof window === "undefined") return;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  private emit(online: boolean): void {
    for (const cb of this.listeners) cb(online);
  }

  async pickFiles(opts?: PickFilesOptions): Promise<PickedFile[]> {
    if (typeof document === "undefined") return [];
    return new Promise<PickedFile[]>((resolve, reject) => {
      const input = document.createElement("input");
      input.type = "file";
      input.multiple = opts?.multiple ?? true;
      if (opts?.accept?.length) input.accept = opts.accept.join(",");
      // The input MUST be in the DOM for .click() to open the picker in Safari /
      // WKWebView (a detached input silently no-ops there); hide it offscreen.
      input.style.position = "fixed";
      input.style.left = "-9999px";
      document.body.appendChild(input);
      let settled = false;
      const cleanup = () => {
        settled = true;
        input.remove();
        window.removeEventListener("focus", onCancel);
      };
      input.onchange = async () => {
        try {
          const files = Array.from(input.files ?? []);
          const picked: PickedFile[] = [];
          for (const f of files) {
            validate(f, opts);
            picked.push({
              name: f.name,
              bytes: new Uint8Array(await f.arrayBuffer()),
              mime: f.type || undefined,
            });
          }
          cleanup();
          resolve(picked);
        } catch (err) {
          cleanup();
          reject(err);
        }
      };
      // Cancelling the dialog fires no 'change' — detect via window focus so the
      // promise resolves ([]) instead of hanging forever.
      const onCancel = () => {
        setTimeout(() => {
          if (!settled && (!input.files || input.files.length === 0)) {
            cleanup();
            resolve([]);
          }
        }, 350);
      };
      window.addEventListener("focus", onCancel, { once: true });
      input.click();
    });
  }

  async pickDirectory(opts?: PickFilesOptions): Promise<PickedFile[]> {
    if (typeof document === "undefined") return [];
    return new Promise<PickedFile[]>((resolve, reject) => {
      const input = document.createElement("input");
      input.type = "file";
      // webkitdirectory yields every file under the chosen folder with a relative path.
      (input as unknown as { webkitdirectory: boolean }).webkitdirectory = true;
      input.multiple = true;
      input.style.position = "fixed";
      input.style.left = "-9999px";
      document.body.appendChild(input);
      let settled = false;
      const cleanup = () => {
        settled = true;
        input.remove();
        window.removeEventListener("focus", onCancel);
      };
      input.onchange = async () => {
        try {
          const files = Array.from(input.files ?? []);
          const picked: PickedFile[] = [];
          for (let i = 0; i < files.length; i++) {
            const f = files[i];
            // Web already holds every File; report progress per file so the shared UI's
            // import row behaves the same as desktop (total known up front).
            opts?.onProgress?.(i + 1, files.length);
            if (opts?.accept?.length && f.type && !opts.accept.includes(f.type)) continue;
            validate(f, opts);
            picked.push({
              name: f.name,
              // Relative path inside the picked folder (e.g. "Designs/Abstract/x.png").
              path: (f as unknown as { webkitRelativePath?: string }).webkitRelativePath || f.name,
              bytes: new Uint8Array(await f.arrayBuffer()),
              mime: f.type || undefined,
            });
          }
          cleanup();
          resolve(picked);
        } catch (err) {
          cleanup();
          reject(err);
        }
      };
      const onCancel = () => {
        setTimeout(() => {
          if (!settled && (!input.files || input.files.length === 0)) {
            cleanup();
            resolve([]);
          }
        }, 350);
      };
      window.addEventListener("focus", onCancel, { once: true });
      input.click();
    });
  }

  async decodeImage(bytes: Uint8Array): Promise<{ width: number; height: number; rgba: Uint8ClampedArray }> {
    if (typeof document === "undefined" || typeof createImageBitmap === "undefined") {
      return { width: 0, height: 0, rgba: new Uint8ClampedArray(0) };
    }
    const blob = new Blob([bytes as BlobPart]);
    const bmp = await createImageBitmap(blob);
    const canvas = document.createElement("canvas");
    canvas.width = bmp.width;
    canvas.height = bmp.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return { width: 0, height: 0, rgba: new Uint8ClampedArray(0) };
    ctx.drawImage(bmp, 0, 0);
    // Zero-copy: return ImageData.data (Uint8ClampedArray) directly — never Array.from().
    const data = ctx.getImageData(0, 0, bmp.width, bmp.height).data;
    return { width: bmp.width, height: bmp.height, rgba: data };
  }

  async exportBundle(bundle: ExportBundle): Promise<ExportBundleResult> {
    const zipped = createZip(
      bundle.files.map((f) => ({ name: f.relPath, bytes: f.bytes })),
    );
    await this.download(`${bundle.name}.zip`, zipped, "application/zip");
    return { written: bundle.files.length };
  }

  async download(name: string, bytes: Uint8Array, mime = "application/octet-stream"): Promise<void> {
    if (typeof document === "undefined") return;
    const blob = new Blob([bytes as BlobPart], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  }

  async ftpUpload(): Promise<number> {
    // Browsers can't open raw FTP sockets — FTP export is a desktop-only capability.
    throw new Error("FTP export is only available in the desktop app");
  }

  // The browser has no device-encrypted secret store; the session store's try/catch falls
  // back to localStorage. `get` returns undefined (no secret) rather than throwing so the
  // hydrate path is a clean no-op; `set`/`delete` throw so the caller keeps using localStorage.
  async secretGet(): Promise<string | undefined> {
    return undefined;
  }

  async secretSet(): Promise<void> {
    throw new Error("Secret store is only available in the desktop app");
  }

  async secretDelete(): Promise<void> {
    throw new Error("Secret store is only available in the desktop app");
  }

  async deviceFingerprint(): Promise<string> {
    const existing = await this.localStore.kv.get<string>(FINGERPRINT_KEY);
    if (existing) return existing;
    const seed = [
      typeof navigator !== "undefined" ? navigator.userAgent : "node",
      typeof navigator !== "undefined" ? navigator.language : "en",
      typeof screen !== "undefined" ? `${screen.width}x${screen.height}` : "0x0",
      Date.now().toString(36),
      Math.random().toString(36).slice(2),
    ].join("|");
    const fp = await sha256Hex(seed);
    await this.localStore.kv.set(FINGERPRINT_KEY, fp);
    return fp;
  }

  isOnline(): boolean {
    return typeof navigator === "undefined" ? true : navigator.onLine;
  }

  onConnectivityChange(cb: ConnectivityListener): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }
}

async function sha256Hex(input: string): Promise<string> {
  if (typeof crypto !== "undefined" && crypto.subtle) {
    const data = new TextEncoder().encode(input);
    const buf = await crypto.subtle.digest("SHA-256", data);
    return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
  }
  // Non-crypto fallback (tests / very old runtimes).
  let h = 0;
  for (let i = 0; i < input.length; i++) h = (Math.imul(31, h) + input.charCodeAt(i)) | 0;
  return (h >>> 0).toString(16).padStart(8, "0");
}

export function createWebPlatform(localStore?: LocalStore): PlatformIO {
  const io = new WebPlatformIO(localStore);
  setCurrentPlatform(io);
  return io;
}
